import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MusicShell from "../fanHome/MusicShell";
import { ACCENT } from "../artistStudio/shared";
import { listInbox } from "../../api/notificationInbox";
import { relativeTime } from "../../utils/relativeTime";

// /rights/notices: the take-down updates a buyer or video maker received when a rights case changed a sound they use: licences
// revoked or refunded, a video muted or hidden, and the later notice when that decision was put back. They are the Rights notices of
// the inbox with the event `rights_enforcement`; the bell still shows them too. Read-only: opening one goes to the sound.
const PAGE_SIZE = 20;
const MAX_PAGES = 10;

export default function BuyerNotices() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setStatus("loading");
    setError("");
    try {
      let all = [];
      let cursor = null;
      for (let page = 0; page < MAX_PAGES; page += 1) {
        const result = await listInbox({ filter: "rights", limit: PAGE_SIZE, cursor });
        all = all.concat((result.items || []).filter((n) => n.event === "rights_enforcement"));
        cursor = result.next_cursor || null;
        if (!cursor) break;
      }
      setItems(all);
      setStatus("ready");
    } catch (e) {
      setError(e.message || "Could not load your notices.");
      setStatus("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <MusicShell maxWidth="max-w-3xl">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Take-down updates</h1>
      <p className="mt-1 text-gray-600">What happened to licences and videos you have when the rights to a sound were questioned, and when a decision was put back.</p>
      {status === "loading" && <p className="mt-6 text-gray-500" role="status">Loading your updates…</p>}
      {status === "error" && (
        <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert">
          {error} <button type="button" onClick={load} className="font-semibold underline">Try again</button>
        </div>
      )}
      {status === "ready" && items.length === 0 && (
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-6 text-center text-gray-600" data-testid="buyer-notices-empty">
          You have no take-down updates. If a sound you use is ever affected, the details will appear here.
        </div>
      )}
      {status === "ready" && items.length > 0 && (
        <ul className="mt-6 space-y-3" data-testid="buyer-notices-list">
          {items.map((n) => (
            <li key={n.id} className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-semibold text-gray-900">{n.title}</span>
                <span className="shrink-0 text-xs text-gray-400">{relativeTime(n.created_at)}</span>
              </div>
              <p className="mt-1 text-sm text-gray-600">{n.message}</p>
              {n.link && n.link.startsWith("/") && (
                <Link to={n.link} className="mt-2 inline-block text-sm font-semibold hover:underline" style={{ color: ACCENT }}>Open the sound</Link>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="mt-6 text-sm text-gray-500">
        If you think a decision is a mistake, contact Pipaan support. <Link to="/rights" className="font-semibold hover:underline" style={{ color: ACCENT }}>Back to Rights &amp; legal</Link>
      </p>
    </MusicShell>
  );
}
